import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FunnelStage } from '../hooks/useOverviewData';
import { formatNumber } from '../utils/format';
import { downloadCsv, todayYmd } from '../utils/csv';
import ExportButton from './ExportButton';

const DEFAULT_ROWS = 8;

interface Props {
    stages: FunnelStage[] | null;
    // Previous-period stage counts — used to render period-over-period
    // deltas. Optional so the overview page (current-period only) can omit
    // it without TS complaining. The deep-dive page passes both periods.
    previousStages?: FunnelStage[] | null;
    loading: boolean;
    // Page passes 3 on mobile to keep this card short; "Show all" still reveals everything.
    defaultRows?: number;
}

/**
 * Stage-by-stage production funnel. For each responsibility:
 *   - bar width is proportional to `reached` (busiest stage = 100% width)
 *   - inside the bar, a fill shows the completion ratio (green)
 *   - rework count is called out separately if > 0 (amber)
 *
 * Stages render in workflow order (backend pre-sorts by avg_sequence).
 */
const ProductionFunnelCard: React.FC<Props> = ({
    stages,
    previousStages,
    loading,
    defaultRows = DEFAULT_ROWS,
}) => {
    const intl = useIntl();
    const locale = intl.locale;
    const [expanded, setExpanded] = useState(false);

    const maxReached = useMemo(
        () => Math.max(...(stages?.map((s) => s.reached) ?? [0]), 1),
        [stages],
    );

    const previousById = useMemo(() => {
        const map = new Map<number, FunnelStage>();
        (previousStages ?? []).forEach((s) => map.set(s.responsibility_id, s));
        return map;
    }, [previousStages]);

    const visibleStages = stages
        ? expanded
            ? stages
            : stages.slice(0, defaultRows)
        : null;
    const hasMore = stages != null && stages.length > defaultRows;

    const handleExport = () => {
        if (!stages || stages.length === 0) return;
        downloadCsv<FunnelStage & { completion_pct: number }>(
            `production-funnel-${todayYmd()}.csv`,
            [
                { header: 'Stage', accessor: (r) => r.name },
                { header: 'Reached', accessor: (r) => r.reached },
                { header: 'Completed', accessor: (r) => r.completed },
                { header: 'Reworked', accessor: (r) => r.reworked },
                { header: 'Completion %', accessor: (r) => r.completion_pct.toFixed(1) },
                { header: 'Avg sequence', accessor: (r) => r.avg_sequence.toFixed(2) },
            ],
            stages.map((s) => ({
                ...s,
                completion_pct: s.reached > 0 ? (s.completed / s.reached) * 100 : 0,
            })),
        );
    };

    return (
        <section className="av2-card">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage
                            id="analyticsV2.funnel.title"
                            defaultMessage="Production funnel"
                        />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.funnel.subtitle"
                            defaultMessage="Stage-by-stage throughput, in workflow order"
                        />
                    </p>
                </div>
                <ExportButton
                    onClick={handleExport}
                    disabled={!stages || stages.length === 0}
                />
            </div>
            <div className="av2-card__body">
                {loading || !stages ? (
                    <FunnelSkeleton count={5} />
                ) : stages.length === 0 ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.funnel.empty"
                            defaultMessage="No production activity in this period."
                        />
                    </div>
                ) : (
                    <ol className="av2-funnel">
                        {(visibleStages ?? []).map((stage, idx) => {
                            const reachedPct = (stage.reached / maxReached) * 100;
                            const completionPct =
                                stage.reached > 0 ? (stage.completed / stage.reached) * 100 : 0;
                            const prev = previousById.get(stage.responsibility_id);
                            const hasCompare = previousStages != null;
                            const reachedDelta = hasCompare && prev
                                ? stage.reached - prev.reached
                                : null;
                            const reachedDeltaPct = hasCompare && prev && prev.reached > 0
                                ? Math.round(((stage.reached - prev.reached) / prev.reached) * 1000) / 10
                                : null;
                            return (
                                <li
                                    key={stage.responsibility_id}
                                    className="av2-funnel__row"
                                    style={{ animationDelay: `${idx * 40}ms` }}
                                >
                                    <div className="av2-funnel__header">
                                        <span className="av2-funnel__name">{stage.name}</span>
                                        <span className="av2-funnel__metrics">
                                            <span className="av2-funnel__completed">
                                                {formatNumber(stage.completed, locale)}
                                            </span>
                                            <span className="av2-funnel__divider">/</span>
                                            <span className="av2-funnel__reached">
                                                {formatNumber(stage.reached, locale)}
                                            </span>
                                            <span className="av2-funnel__pct">
                                                {completionPct.toFixed(0)}%
                                            </span>
                                        </span>
                                    </div>
                                    <div className="av2-funnel__track" style={{ width: `${reachedPct}%` }}>
                                        <div
                                            className="av2-funnel__fill"
                                            style={{ width: `${completionPct}%` }}
                                        />
                                    </div>
                                    {stage.reworked > 0 && (
                                        <div className="av2-funnel__rework">
                                            <span className="av2-funnel__rework-dot" aria-hidden="true" />
                                            <FormattedMessage
                                                id="analyticsV2.funnel.reworked"
                                                defaultMessage="{count, plural, one {# product reworked} other {# products reworked}}"
                                                values={{ count: stage.reworked }}
                                            />
                                        </div>
                                    )}
                                    {hasCompare && reachedDelta != null && (
                                        <div
                                            className={`av2-funnel__period av2-funnel__period--${
                                                reachedDelta > 0
                                                    ? 'up'
                                                    : reachedDelta < 0
                                                    ? 'down'
                                                    : 'flat'
                                            }`}
                                        >
                                            <FormattedMessage
                                                id="analyticsV2.funnel.vsLast"
                                                defaultMessage="vs last period"
                                            />
                                            <span className="av2-funnel__period-arrow" aria-hidden="true">
                                                {reachedDelta > 0 ? '↑' : reachedDelta < 0 ? '↓' : '→'}
                                            </span>
                                            <span className="av2-funnel__period-value">
                                                {reachedDelta > 0 ? '+' : ''}
                                                {formatNumber(reachedDelta, locale)}
                                                {reachedDeltaPct != null && reachedDeltaPct !== 0 && (
                                                    <>
                                                        {' '}
                                                        ({reachedDeltaPct > 0 ? '+' : ''}
                                                        {reachedDeltaPct}%)
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                        {hasMore && (
                            <button
                                type="button"
                                className={`av2-show-all${expanded ? ' av2-show-all--expanded' : ''}`}
                                onClick={() => setExpanded((v) => !v)}
                            >
                                {expanded ? (
                                    <FormattedMessage
                                        id="analyticsV2.showLess"
                                        defaultMessage="Show less"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="analyticsV2.showAll"
                                        defaultMessage="Show all ({count})"
                                        values={{ count: stages!.length }}
                                    />
                                )}
                                <span className="av2-show-all__chevron" aria-hidden="true">
                                    ⌄
                                </span>
                            </button>
                        )}
                    </ol>
                )}
            </div>
        </section>
    );
};

const FunnelSkeleton: React.FC<{ count: number }> = ({ count }) => {
    return (
        <ol className="av2-funnel">
            {Array.from({ length: count }, (_, i) => (
                <li key={i} className="av2-funnel__row av2-funnel__row--skeleton">
                    <div className="av2-funnel__header">
                        <div className="av2-skel av2-skel--funnel-name" />
                        <div className="av2-skel av2-skel--funnel-meta" />
                    </div>
                    <div className="av2-skel av2-skel--funnel-bar" />
                </li>
            ))}
        </ol>
    );
};

export default ProductionFunnelCard;
